// ═══════════════════════════════════════
// KEY-PICKER — Dropdown sélecteur de tonalité
// ═══════════════════════════════════════

import { updateKeyFromForm } from './header-form.js';
import { highlight } from './editor.js';
import { render } from './renderer.js';

// ── Données des tonalités ─────────────────────────────────────────────────────

const KEYS = {
    major: [
        { label: 'Do',   abc: 'C',  sharps: 0, flats: 0 },
        { label: 'Sol',  abc: 'G',  sharps: 1, flats: 0 },
        { label: 'Ré',   abc: 'D',  sharps: 2, flats: 0 },
        { label: 'La',   abc: 'A',  sharps: 3, flats: 0 },
        { label: 'Mi',   abc: 'E',  sharps: 4, flats: 0 },
        { label: 'Si',   abc: 'B',  sharps: 5, flats: 0 },
        { label: 'Fa#',  abc: 'F#', sharps: 6, flats: 0 },
        { label: 'Do#',  abc: 'C#', sharps: 7, flats: 0 },
        { label: 'Fa',   abc: 'F',  sharps: 0, flats: 1 },
        { label: 'Sib',  abc: 'Bb', sharps: 0, flats: 2 },
        { label: 'Mib',  abc: 'Eb', sharps: 0, flats: 3 },
        { label: 'Lab',  abc: 'Ab', sharps: 0, flats: 4 },
        { label: 'Réb',  abc: 'Db', sharps: 0, flats: 5 },
        { label: 'Solb', abc: 'Gb', sharps: 0, flats: 6 },
        { label: 'Dob',  abc: 'Cb', sharps: 0, flats: 7 },
    ],
    minor: [
        { label: 'La m',   abc: 'Am',  sharps: 0, flats: 0 },
        { label: 'Mi m',   abc: 'Em',  sharps: 1, flats: 0 },
        { label: 'Si m',   abc: 'Bm',  sharps: 2, flats: 0 },
        { label: 'Fa# m',  abc: 'F#m', sharps: 3, flats: 0 },
        { label: 'Do# m',  abc: 'C#m', sharps: 4, flats: 0 },
        { label: 'Sol# m', abc: 'G#m', sharps: 5, flats: 0 },
        { label: 'Ré# m',  abc: 'D#m', sharps: 6, flats: 0 },
        { label: 'La# m',  abc: 'A#m', sharps: 7, flats: 0 },
        { label: 'Ré m',   abc: 'Dm',  sharps: 0, flats: 1 },
        { label: 'Sol m',  abc: 'Gm',  sharps: 0, flats: 2 },
        { label: 'Do m',   abc: 'Cm',  sharps: 0, flats: 3 },
        { label: 'Fa m',   abc: 'Fm',  sharps: 0, flats: 4 },
        { label: 'Sib m',  abc: 'Bbm', sharps: 0, flats: 5 },
        { label: 'Mib m',  abc: 'Ebm', sharps: 0, flats: 6 },
        { label: 'Lab m',  abc: 'Abm', sharps: 0, flats: 7 },
    ],
};

// Valeurs ABC reconnues → métadonnées pour le SVG
const KEY_META = {};
[...KEYS.major, ...KEYS.minor].forEach(k => { KEY_META[k.abc] = k; });

// Clés spéciales cornemuse (pour le SVG uniquement)
const PIPES_META = {
    'HP':   { sharps: 0, flats: 0, pipes: 'hp'  },
    'Hp':   { sharps: 0, flats: 0, pipes: 'hp2' },
    'D =G': { sharps: 0, flats: 0, pipes: 'dg'  },
};

// ── Dessin SVG d'une armure ───────────────────────────────────────────────────

function _drawStaff(sharps, flats, pipes) {
    const W = 54, H = 36;
    const lineYs = [4, 10, 16, 22, 28];

    let svg = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">`;
    lineYs.forEach(y => {
        svg += `<line x1="1" y1="${y}" x2="${W - 1}" y2="${y}" stroke="currentColor" opacity="0.3" stroke-width="0.8"/>`;
    });

    if (pipes === 'hp') {
        svg += `<text x="${W/2}" y="19" text-anchor="middle" font-size="8" fill="currentColor" opacity="0.4" font-style="italic">aucune</text>`;
    } else if (pipes === 'hp2' || pipes === 'dg') {
        const sharpData = [{ x: 2, cy: 4 }, { x: 9, cy: 16 }];
        sharpData.forEach(({ x, cy }) => {
            svg += `<text x="${x}" y="${cy + 4}" font-size="11" fill="#c8a96e" font-family="serif" font-weight="bold">#</text>`;
        });
        svg += `<text x="25" y="${13 + 4}" font-size="11" fill="#6eb5e8" font-family="serif">♮</text>`;
        if (pipes === 'dg') {
            svg += `<text x="38" y="33" font-size="7" fill="currentColor" opacity="0.4" font-family="monospace">D=G</text>`;
        }
    } else if (sharps > 0) {
        const SHARP_CY = [4, 16, 1, 13, 25, 10, 22];
        let x = 2;
        for (let i = 0; i < sharps; i++) {
            svg += `<text x="${x}" y="${SHARP_CY[i] + 4}" font-size="11" fill="#c8a96e" font-family="serif" font-weight="bold">#</text>`;
            x += 6;
        }
    } else if (flats > 0) {
        const FLAT_CY = [16, 7, 19, 10, 22, 13, 25];
        let x = 2;
        for (let i = 0; i < flats; i++) {
            svg += `<text x="${x}" y="${FLAT_CY[i] + 4}" font-size="13" fill="#c8a96e" font-family="serif">♭</text>`;
            x += 7;
        }
    } else {
        svg += `<text x="${W/2}" y="19" text-anchor="middle" font-size="9" fill="currentColor" opacity="0.4">∅</text>`;
    }

    svg += '</svg>';
    return svg;
}

function _staffForAbc(abc) {
    if (PIPES_META[abc]) return _drawStaff(0, 0, PIPES_META[abc].pipes);
    const m = KEY_META[abc];
    if (m) return _drawStaff(m.sharps, m.flats);
    return _drawStaff(0, 0); // inconnu → portée vide
}

// ── Instances actives ─────────────────────────────────────────────────────────

const _instances = {};

function _getInstance(triggerId) {
    if (!_instances[triggerId]) {
        _instances[triggerId] = {
            triggerId,
            // mode : 'inherit' | 'none' | 'instrument' | 'major' | 'minor'
            mode:          'major',
            currentAbc:    'G',
            onSelect:      null,
            // options pour les modales voix
            allowInherit:  false,   // true si voix > 0
            suggestedKeys: [],      // ['HP','Hp','D =G'] selon instrument
        };
    }
    return _instances[triggerId];
}

// ── Rendu du dropdown ─────────────────────────────────────────────────────────

function _render(inst) {
    const dd = document.getElementById('key-picker-dropdown');
    if (!dd) return;

    // ── Boutons radio ──
    const radios = [];
    if (inst.allowInherit) {
        radios.push({ id: 'inherit',    label: 'Héritée' });
    }
    radios.push({ id: 'none',      label: 'Aucune' });
    if (inst.suggestedKeys.length) {
        radios.push({ id: 'instrument', label: 'Instrument' });
    }
    radios.push({ id: 'major', label: 'Majeur' });
    radios.push({ id: 'minor', label: 'Mineur' });

    let html = `<div class="kp-modes">`;
    radios.forEach(r => {
        html += `<label class="kp-mode-label${inst.mode === r.id ? ' active' : ''}">
            <input type="radio" name="kp-mode" value="${r.id}"${inst.mode === r.id ? ' checked' : ''}>
            ${r.label}
        </label>`;
    });
    html += `</div>`;

    // ── Grille de tonalités (absente pour inherit et none) ──
    if (inst.mode !== 'inherit' && inst.mode !== 'none') {
        let keys = [];
        if (inst.mode === 'instrument') {
            keys = inst.suggestedKeys.map(abc => ({ abc, label: abc }));
        } else {
            keys = KEYS[inst.mode]; // major | minor
        }

        html += `<div class="kp-grid">`;
        keys.forEach(k => {
            const sel = k.abc === inst.currentAbc ? ' selected' : '';
            html += `<button class="kp-key${sel}" data-abc="${k.abc}" title="${k.label ?? k.abc}">
                ${_staffForAbc(k.abc)}
                <span class="kp-label">${k.label ?? k.abc}</span>
            </button>`;
        });
        html += `</div>`;
    }

    dd.innerHTML = html;

    // listeners radios
    dd.querySelectorAll('input[name="kp-mode"]').forEach(radio => {
        radio.addEventListener('change', e => {
            inst.mode = e.target.value;
            if (inst.mode === 'inherit') {
                _select(inst, '__inherit__');
            } else if (inst.mode === 'none') {
                _select(inst, 'none');
            } else {
                _render(inst); // afficher la grille
            }
        });
    });

    // listeners grille
    dd.querySelectorAll('.kp-key').forEach(btn => {
        btn.addEventListener('mousedown', e => {
            e.preventDefault();
            e.stopPropagation();
            _select(inst, btn.dataset.abc);
        });
    });
}

function _labelFromAbc(abc) {
    if (abc === '__inherit__') return 'Héritée';
    if (abc === 'none')        return 'Aucune';
    if (PIPES_META[abc])       return abc;
    for (const tab of Object.values(KEYS)) {
        const found = tab.find(k => k.abc === abc);
        if (found) return found.label;
    }
    return abc;
}

function _modeFromAbc(abc, suggestedKeys = []) {
    if (abc === '__inherit__') return 'inherit';
    if (abc === 'none')        return 'none';
    if (suggestedKeys.includes(abc)) return 'instrument';
    if (KEYS.minor.find(k => k.abc === abc)) return 'minor';
    return 'major';
}

function _select(inst, abc) {
    inst.currentAbc = abc;
    // Recalcule le mode pour cocher le bon radio au prochain open
    inst.mode = _modeFromAbc(abc, inst.suggestedKeys);
    _updateTrigger(inst);

    if (inst.onSelect) {
        inst.onSelect(abc, _labelFromAbc(abc));
    } else {
        const input = document.getElementById('hf-key-value');
        if (input) input.value = abc;
        _applyKey(abc);
    }
    _close();
}

function _applyKey(abc) {
    const el    = document.getElementById('abc');
    const lines = el.textContent.split('\n');
    const kIdx  = lines.findIndex(l => /^K:/.test(l));
    if (kIdx !== -1) lines[kIdx] = 'K:' + abc;
    else lines.push('K:' + abc);
    el.textContent = lines.join('\n');
    highlight();
    render();
}

function _updateTrigger(inst) {
    const trigger = document.getElementById(inst.triggerId);
    if (!trigger) return;
    trigger.textContent = _labelFromAbc(inst.currentAbc);
}

// ── Positionnement ────────────────────────────────────────────────────────────

function _position(triggerId) {
    const trigger = document.getElementById(triggerId);
    const dd      = document.getElementById('key-picker-dropdown');
    if (!trigger || !dd) return;

    const rect = trigger.getBoundingClientRect();
    dd.style.top  = (rect.bottom + window.scrollY + 4) + 'px';
    dd.style.left = (rect.left   + window.scrollX)     + 'px';

    requestAnimationFrame(() => {
        const r = dd.getBoundingClientRect();
        if (r.right > window.innerWidth - 8) {
            dd.style.left = (window.innerWidth - r.width - 8) + 'px';
        }
        if (r.bottom > window.innerHeight - 8) {
            dd.style.top = (rect.top + window.scrollY - r.height - 4) + 'px';
        }
    });
}

// ── Ouverture / fermeture ─────────────────────────────────────────────────────

let _activeTriggerId = null;

function _open(inst) {
    const dd = document.getElementById('key-picker-dropdown');
    if (!dd) return;
    _activeTriggerId = inst.triggerId;
    _render(inst);
    _position(inst.triggerId);
    dd.style.display = 'block';
}

function _close() {
    const dd = document.getElementById('key-picker-dropdown');
    if (dd) dd.style.display = 'none';
    _activeTriggerId = null;
}

export function isKeyPickerOpen() {
    const dd = document.getElementById('key-picker-dropdown');
    return !!(dd && dd.style.display === 'block');
}

// ── API publique ──────────────────────────────────────────────────────────────

export function setKeyPickerValue(abc) {
    const inst = _getInstance('hf-key-trigger');
    inst.currentAbc = abc;
    inst.mode       = _modeFromAbc(abc, inst.suggestedKeys);
    _updateTrigger(inst);
}

export function initKeyPicker() {
    const triggerId = 'hf-key-trigger';
    const inst      = _getInstance(triggerId);
    inst.onSelect   = null;

    const trigger = document.getElementById(triggerId);
    if (!trigger) return;

    trigger.addEventListener('click', e => {
        e.stopPropagation();
        const dd = document.getElementById('key-picker-dropdown');
        if (dd && dd.style.display === 'block' && _activeTriggerId === triggerId) {
            _close();
        } else {
            _open(inst);
        }
    });

    document.addEventListener('mousedown', e => {
        const dd = document.getElementById('key-picker-dropdown');
        if (!dd) return;
        if (!dd.contains(e.target) && e.target.id !== triggerId) _close();
    });
}

/**
 * Init pour un trigger externe (ex: modale voix).
 * @param {string}   triggerId
 * @param {string}   initialAbc     — valeur ABC courante, '__inherit__' ou 'none'
 * @param {Function} onSelect       — callback(abc, label)
 * @param {object}   opts
 * @param {boolean}  opts.allowInherit   — afficher l'option "Héritée" (voix > 0)
 * @param {string[]} opts.suggestedKeys  — tonalités suggérées par l'instrument
 */
export function initKeyPickerFor(triggerId, initialAbc, onSelect, opts = {}) {
    const inst           = _getInstance(triggerId);
    inst.allowInherit    = opts.allowInherit  ?? false;
    inst.suggestedKeys   = opts.suggestedKeys ?? [];
    inst.currentAbc      = initialAbc || (inst.allowInherit ? '__inherit__' : 'HP');
    inst.mode            = _modeFromAbc(inst.currentAbc, inst.suggestedKeys);
    inst.onSelect        = onSelect;

    _updateTrigger(inst);

    const trigger = document.getElementById(triggerId);
    if (!trigger) return;

    const fresh = trigger.cloneNode(true);
    trigger.parentNode.replaceChild(fresh, trigger);

    fresh.addEventListener('click', e => {
        e.stopPropagation();
        const dd = document.getElementById('key-picker-dropdown');
        if (dd && dd.style.display === 'block' && _activeTriggerId === triggerId) {
            _close();
        } else {
            _open(inst);
        }
    });

    if (!_globalMousedownRegistered) {
        _globalMousedownRegistered = true;
        document.addEventListener('mousedown', e => {
            const dd = document.getElementById('key-picker-dropdown');
            if (!dd) return;
            const activeTrigger = _activeTriggerId ? document.getElementById(_activeTriggerId) : null;
            if (!dd.contains(e.target) && e.target !== activeTrigger) _close();
        });
    }
}

let _globalMousedownRegistered = false;


export function getKeyPickerInstance(triggerId) {
    return _instances[triggerId] ?? null;
}
